import { useCallback } from 'react'
import gql from 'graphql-tag'
import type { Asset, PublicationStatusEnum } from '@ztechapps/common.contract'
import type { UseMutationProps } from '../useMutation'
import { useMutation } from '../useMutation'
import type { AssetEntityType } from './useCreateAsset'

/**
 * Input for updating an asset on an entity.
 */
export type UpdateAssetInput = {
  title?: string
  subtitle?: string
  url?: string
  type?: string
  status?: PublicationStatusEnum
}

/**
 * Arguments for the update asset mutation.
 * The filter must include both the entity _id and the asset _id.
 */
export type UpdateAssetArgs = {
  filter: { _id: string; 'assets._id': string }
  input: UpdateAssetInput
}

/**
 * Result type for useUpdateAsset hook
 */
export type UseUpdateAssetResult = [
  (args: UpdateAssetArgs) => Promise<{ _id: string; assets: Asset[] } | undefined>,
  { error: any; loading: boolean },
]

/**
 * Builds the GraphQL mutation query for updating an asset on a specific entity type.
 * Uses child resolver pattern: update{EntityType}_assets
 */
const buildUpdateAssetQuery = (entityType: AssetEntityType) => {
  const mutationName = `update${entityType}_assets`

  const queryString = `
    mutation ${mutationName}($filter: JSON!, $input: JSON!) {
      ${mutationName}(filter: $filter, input: $input) {
        _id
        assets {
          _id
          url
          title
          subtitle
          type
          status
          thumbUrl
          mediumUrl
          largeUrl
        }
      }
    }
  `

  return gql(queryString)
}

/**
 * Hook for updating an asset on an entity using child resolver mutations.
 *
 * Uses the pattern update{EntityType}_assets which:
 * - Triggers the AssetChildRepository.preSave hook for URL sanitization
 * - Provides atomic updates avoiding race conditions
 * - Updates only the specific asset, not the entire array
 *
 * @param entityType - The entity type (Product, Order, Task, Project, PurchaseOrder)
 * @param props - Optional mutation configuration
 * @returns Tuple of [executeFunction, { error, loading }]
 *
 * @example
 * ```tsx
 * const [updateAsset, { loading }] = useUpdateAsset('Product')
 *
 * await updateAsset({
 *   filter: { _id: productId, 'assets._id': assetId },
 *   input: { title: 'Updated Title' }
 * })
 * ```
 */
export const useUpdateAsset = (
  entityType: AssetEntityType,
  { query, ...props } = {} as UseMutationProps
): UseUpdateAssetResult => {
  const defaultQuery = buildUpdateAssetQuery(entityType)

  const [executeMutation, , { error, loading }] = useMutation<{ _id: string; assets: Asset[] }>({
    query: query ?? defaultQuery,
    ...props,
  })

  // Wrap the mutation to properly extract filter and input from UpdateAssetArgs
  const executeUpdate = useCallback(
    async (args: UpdateAssetArgs) => {
      const result = await executeMutation({
        filter: args.filter,
        input: args.input,
      })
      return result?.data ? (Object.values(result.data)[0] as unknown as { _id: string; assets: Asset[] }) : undefined
    },
    [executeMutation]
  )

  return [executeUpdate, { error, loading }]
}
